'use client';

import { Grid, GridItem, Link } from '@coolblue-development/freddy';
import { ArrowDown } from '@coolblue-development/icons';
import { AccessoryProductCardProvider } from '@features/add-to-cart-page/components/Sections/AccessoryProductCard/AccessoryProductCard.context';
import ProductCard from '@features/add-to-cart-page/components/Sections/ProductCard/ProductCard';
import { ATCPageAutomatedSection } from '@features/add-to-cart-page/typings/AutomatedSection';
import type {
  ATCPageProductTypeSection,
  ATCPageProductTypeSectionSlot
} from '@features/add-to-cart-page/typings/ProductTypeSection';
import { shouldKeepPromoLabelSpace } from '@features/shared/PromotionalContent/shouldKeepPromoLabelSpace';
import useTestId from '@hooks/useTestId';
import { useTranslation } from '@i18n/useTranslation';
import { useState } from 'react';

import { useSummaryStore } from '../SummaryStore';

export function ProductCardSectionContent({
  section,
  mainProductId,
  isAutomated = false,
  shouldUsePromotionalContent
}: {
  section: ATCPageProductTypeSection | ATCPageAutomatedSection;
  mainProductId: number;
  isAutomated?: boolean;
  shouldUsePromotionalContent: boolean;
}) {
  const { t } = useTranslation('webshop.addToCart');
  const testId = useTestId('product-card');

  const [showMore, setShowMore] = useState<boolean>(false);

  const filteredSlots = showMore ? section.slots : section.slots.slice(0, 6);
  const { mainProductQuantity } = useSummaryStore();

  return (
    <>
      <Grid gap={400} mt={400}>
        {filteredSlots.map((slot, index) => {
          const product = slot.slotProduct;
          const highlightedAlternative = !isAutomated
            ? (slot as ATCPageProductTypeSectionSlot).highlightedAlternative
            : undefined;

          return (
            <GridItem
              column={{ xs: 'span 12', md: 'span 6', lg: 'span 4' }}
              display='grid'
              key={`accessory-card-container-${section.id}-${slot.slotProduct.id}`}
              {...testId}
            >
              <AccessoryProductCardProvider
                initialProduct={product}
                initialQuantity={mainProductQuantity}
              >
                <ProductCard
                  product={product}
                  highlightedAlternative={highlightedAlternative}
                  slot={slot}
                  mainProductId={mainProductId}
                  accessoryProductCardIndex={index + 1}
                  accessoryProductCardTotalIndices={section.slots.length}
                  isAutomated={isAutomated}
                  showPromoLabelPlaceholder={shouldKeepPromoLabelSpace(
                    filteredSlots.map(product => product.slotProduct)
                  )}
                  shouldUsePromotionalContent={shouldUsePromotionalContent}
                />
              </AccessoryProductCardProvider>
            </GridItem>
          );
        })}
      </Grid>
      {!showMore && section.slots.length > 6 && (
        <Link
          component='button'
          startIcon={<ArrowDown />}
          onClick={() => setShowMore(true)}
          mt={400}
        >
          {t('seeMoreAccessories')}
        </Link>
      )}
    </>
  );
}
